const STRAPI_API_URL = import.meta.env.VITE_STRAPI_API_URL;

const AUTH_STORAGE_KEY = 'pavilion_auth';

const isBrowser = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

// Se usa localStorage para que la sesion persista entre pestanas y recargas
// (el problema anterior era sessionStorage: se perdia al abrir una pestana
// nueva o en algunos flujos de navegacion). El JWT sigue siendo legible por
// JS (limitacion inherente a un SPA sin backend propio); si se necesita
// mayor seguridad, mover el login a un proxy que emita una cookie httpOnly.
export const getStoredAuth = () => {
  if (!isBrowser()) return null;

  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed?.jwt ? parsed : null;
  } catch {
    return null;
  }
};

export const setStoredAuth = (auth) => {
  if (!isBrowser()) return;
  window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(auth));
};

export const clearStoredAuth = () => {
  if (!isBrowser()) return;
  window.localStorage.removeItem(AUTH_STORAGE_KEY);
};

/**
 * Login contra el plugin Users & Permissions de Strapi.
 * POST {STRAPI_API_URL}/auth/local -> { jwt, user }
 *
 * Lanza errores con codigo estable para que el caller decida el mensaje:
 *   - MISSING_CREDENTIALS
 *   - NETWORK_ERROR      (no se pudo contactar a Strapi)
 *   - INVALID_CREDENTIALS (401/403 u otra respuesta no-ok)
 */
export const loginWithStrapi = async (identifier, password) => {
  const trimmedIdentifier = String(identifier || '').trim();
  const trimmedPassword = String(password || '');

  if (!trimmedIdentifier || !trimmedPassword) {
    throw new Error('MISSING_CREDENTIALS');
  }

  let response;
  try {
    response = await fetch(`${STRAPI_API_URL}/auth/local`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier: trimmedIdentifier, password: trimmedPassword }),
    });
  } catch {
    throw new Error('NETWORK_ERROR');
  }

  if (!response.ok) {
    // No exponemos el detalle que devuelve Strapi (evita filtrar si el
    // usuario existe o no); el caller decide el mensaje generico a mostrar.
    throw new Error('INVALID_CREDENTIALS');
  }

  const data = await response.json();
  if (!data?.jwt) {
    throw new Error('INVALID_CREDENTIALS');
  }

  const auth = { jwt: data.jwt, user: data.user || null };
  setStoredAuth(auth);
  return auth;
};

/**
 * Valida el JWT guardado contra Strapi y refresca los datos del usuario.
 *
 * Devuelve { status, user }:
 *   - status 'ok'       -> token valido, user presente
 *   - status 'invalid'  -> Strapi rechazo el token (401/403): hay que cerrar sesion
 *   - status 'network'  -> no se pudo contactar a Strapi: NO se cierra sesion
 *                          (falla transitoria; se conserva el token y se reintenta luego)
 */
export const fetchCurrentUser = async (jwt) => {
  if (!jwt) return { status: 'invalid', user: null };

  try {
    const response = await fetch(`${STRAPI_API_URL}/users/me`, {
      headers: { Authorization: `Bearer ${jwt}` },
    });

    if (response.status === 401 || response.status === 403) {
      return { status: 'invalid', user: null };
    }
    if (!response.ok) {
      // 5xx u otro: lo tratamos como fallo transitorio, no cerramos sesion.
      return { status: 'network', user: null };
    }
    return { status: 'ok', user: await response.json() };
  } catch {
    // fetch throw => sin red o CORS: no cerramos sesion por esto.
    return { status: 'network', user: null };
  }
};
